/**
 * Point-of-Care — vincula um kit ao prontuário do paciente no momento da abertura
 * em sala cirúrgica. Não armazena dados clínicos: apenas FK para futura cascata
 * de recall biológico.
 */
import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ScanLine, Boxes, Stethoscope, Send, CheckCircle2, User } from 'lucide-react';
import { apiGet, apiPost, ApiError } from '@/shared/api/client';
import { useBarcodeScanner } from '@/shared/scanner/GlobalBarcodeListener';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';

interface KitByBarcode {
  id: string;
  barcode: string;
  alias?: string | null;
  template_name?: string | null;
  sterile_expires_at?: string | null;
  is_quarantined?: number;
  current_phase?: string | null;
}

export function PointOfCarePage() {
  const [kit, setKit] = useState<KitByBarcode | null>(null);
  const [surgeryExternalId, setSurgeryExternalId] = useState('');
  const [patientEhrId, setPatientEhrId] = useState('');
  const [surgeon, setSurgeon] = useState('');
  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);

  const flash = (ok: boolean, text: string) => setFeedback({ ok, text });

  const onScan = useCallback(async (code: string) => {
    try {
      const k = await apiGet<KitByBarcode>(`/v1/kit-instances/by-barcode/${encodeURIComponent(code)}`);
      if (k.is_quarantined) { flash(false, `Kit ${k.barcode} em quarentena. Não pode ser aberto.`); return; }
      setKit(k);
      flash(true, `Kit carregado.`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) flash(false, `Kit ${code} não encontrado.`);
      else flash(false, (e as Error).message);
    }
  }, []);

  useBarcodeScanner({ onScan, minLength: 4, captureInInputs: false });

  const link = useMutation({
    mutationFn: async () => {
      // 1) Vincula kit ao paciente
      await apiPost('/v1/surgery/link', {
        kit_instance_id: kit!.id,
        surgery_external_id: surgeryExternalId.trim(),
        patient_ehr_id: patientEhrId.trim(),
        surgeon_name: surgeon || undefined,
      }, { idempotencyKey: `link-${kit!.id}-${surgeryExternalId.trim()}` });
      // 2) Avança FSM (point-of-care)
      await apiPost('/v1/lifecycle/scan', {
        barcode: kit!.barcode, target_phase: 'POINT_OF_CARE',
      }, { idempotencyKey: `poc-${kit!.id}-${Date.now()}` });
    },
    onSuccess: () => {
      flash(true, 'Kit aberto em sala e vinculado ao paciente.');
      setKit(null); setSurgeryExternalId(''); setPatientEhrId(''); setSurgeon('');
    },
    onError: (e) => {
      if (e instanceof ApiError) flash(false, `${e.code}: ${e.message}`);
      else flash(false, (e as Error).message);
    },
  });

  const formValid = !!kit && surgeryExternalId.trim().length > 0 && patientEhrId.trim().length > 0;

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
          Point-of-Care — Abertura em sala
        </h1>
        <p className="text-sm text-neutral-500">
          Circulante: bipe o kit aberto, informe o prontuário do paciente e o protocolo cirúrgico.
        </p>
      </header>

      {feedback ? (
        <div className={`rounded border-2 px-4 py-3 text-md font-medium ${feedback.ok ? 'border-success bg-success-bg text-success' : 'border-danger bg-danger-bg text-danger animate-pulse'}`}>
          {feedback.text}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader title="Kit aberto" />
          <CardBody className="space-y-3">
            {kit ? (
              <>
                <div className="flex items-center gap-3">
                  <span className="grid h-12 w-12 place-items-center rounded bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
                    <Boxes size={24} strokeWidth={1.75} />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-md font-semibold">{kit.alias ?? kit.template_name ?? 'Kit'}</p>
                    <p className="font-mono text-xs text-neutral-500">{kit.barcode}</p>
                  </div>
                </div>
                <dl className="grid grid-cols-2 gap-y-1 text-xs">
                  <dt className="text-neutral-500">Validade</dt>
                  <dd className="font-mono">{kit.sterile_expires_at ?? '—'}</dd>
                  <dt className="text-neutral-500">Fase</dt>
                  <dd className="font-medium">{kit.current_phase ?? '—'}</dd>
                </dl>
                <Button variant="ghost" size="sm" onClick={() => setKit(null)}>Trocar kit</Button>
              </>
            ) : (
              <div className="grid h-40 place-items-center text-center">
                <div>
                  <ScanLine size={36} strokeWidth={1.5} className="mx-auto mb-2 text-neutral-400" />
                  <p className="text-sm text-neutral-500">Bipe o código do kit aberto na sala.</p>
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader title="Vínculo com cirurgia" subtitle="Dados conferidos com o ERP / SIH" />
          <CardBody className="space-y-3">
            <Input
              label="ID externo da cirurgia (agendamento/SIH)"
              placeholder="ex.: SC-2026-04299"
              value={surgeryExternalId}
              onChange={(e) => setSurgeryExternalId(e.target.value)}
              leadingIcon={<Stethoscope size={16} />}
            />
            <Input
              label="ID do prontuário do paciente"
              placeholder="ex.: PRT-998877"
              value={patientEhrId}
              onChange={(e) => setPatientEhrId(e.target.value)}
              leadingIcon={<User size={16} />}
              hint="Apenas FK. ICTUS CME não armazena dados clínicos."
            />
            <Input
              label="Cirurgião responsável (opcional)"
              placeholder="Dr(a). Nome"
              value={surgeon}
              onChange={(e) => setSurgeon(e.target.value)}
            />
            <div className="flex flex-wrap items-center justify-end gap-3 border-t border-neutral-200 pt-3 dark:border-neutral-700">
              {kit ? <Badge tone="brand">Kit pronto</Badge> : <Badge tone="neutral">Aguardando kit</Badge>}
              <Button
                variant="primary"
                size="touch"
                leadingIcon={<Send size={18} />}
                disabled={!formValid}
                loading={link.isPending}
                onClick={() => link.mutate()}
              >
                {link.isPending ? 'Vinculando…' : 'Vincular e abrir kit'}
              </Button>
              {link.isSuccess ? <CheckCircle2 className="text-success" size={20} /> : null}
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
